import { trpc } from "@/lib/trpc";
import { formatDollars } from "@payrollmart-invoice/shared";
import { ChevronDown, RefreshCw } from "lucide-react";
import { useState } from "react";

export function QboInvoiceHistory() {
	const { data: qboStatus } = trpc.qbo.status.useQuery();
	const {
		data: qboInvoices,
		isLoading,
		refetch,
		isRefetching,
	} = trpc.qbo.listAllInvoices.useQuery(undefined, { enabled: qboStatus?.connected === true });
	const [collapsed, setCollapsed] = useState(true);

	if (!qboStatus?.connected) return null;

	const invoiceItems = qboInvoices?.items ?? [];
	// Paginated endpoint returns { items, hasMore, page, pageSize }; this view
	// currently shows only the first page. `count` is the number shown, not the
	// total in QBO. Wire a Load More button later if needed.
	const count = invoiceItems.length;

	const qboStatusColors: Record<string, string> = {
		paid: "bg-[#f0fdf4] text-[#16a34a]",
		open: "bg-[#eef2ff] text-[#4f46e5]",
		overdue: "bg-[#fef2f2] text-[#dc2626]",
	};

	return (
		<div className="rounded-lg border border-[#e5e7eb] bg-white">
			<div className="flex items-center justify-between px-6 py-4">
				<button
					type="button"
					onClick={() => setCollapsed(!collapsed)}
					className="flex items-center gap-2"
				>
					<ChevronDown
						className={`h-4 w-4 text-[#6b7280] transition-transform ${collapsed ? "-rotate-90" : ""}`}
						strokeWidth={1.5}
					/>
					<h2 className="text-xs font-medium uppercase tracking-wide text-[#6b7280]">
						QuickBooks Invoice History{!isLoading && ` (${count})`}
					</h2>
				</button>
				<button
					type="button"
					onClick={() => refetch()}
					disabled={isRefetching}
					className="inline-flex items-center gap-1.5 rounded-md border border-[#e5e7eb] bg-white px-3 py-1.5 text-xs font-medium text-[#1e1e20] transition-colors hover:bg-[#f8f9fb] disabled:opacity-50"
				>
					<RefreshCw
						className={`h-3 w-3 ${isRefetching ? "animate-spin" : ""}`}
						strokeWidth={1.5}
					/>
					{isRefetching ? "Refreshing..." : "Refresh"}
				</button>
			</div>
			{!collapsed &&
				(isLoading ? (
					<div className="divide-y divide-[#f3f4f6] border-t border-[#f3f4f6]">
						{[1, 2, 3].map((i) => (
							<div key={i} className="h-11 animate-pulse bg-[#f8f9fb]" />
						))}
					</div>
				) : count === 0 ? (
					<div className="border-t border-[#f3f4f6] py-12 text-center text-sm text-[#9ca3af]">
						No invoices found in QuickBooks.
					</div>
				) : (
					<div className="overflow-x-auto border-t border-[#f3f4f6]">
						<table className="w-full text-sm">
							<thead>
								<tr className="border-b-2 border-[#e5e7eb] bg-[#f8f9fb] text-left">
									<th className="px-4 py-2.5 text-xs font-medium uppercase tracking-wide text-[#6b7280]">
										Invoice #
									</th>
									<th className="px-4 py-2.5 text-xs font-medium uppercase tracking-wide text-[#6b7280]">
										Customer
									</th>
									<th className="px-4 py-2.5 text-xs font-medium uppercase tracking-wide text-[#6b7280]">
										Date
									</th>
									<th className="px-4 py-2.5 text-xs font-medium uppercase tracking-wide text-[#6b7280]">
										Due Date
									</th>
									<th className="px-4 py-2.5 text-xs font-medium uppercase tracking-wide text-[#6b7280]">
										Status
									</th>
									<th className="px-4 py-2.5 text-right text-xs font-medium uppercase tracking-wide text-[#6b7280]">
										Amount
									</th>
									<th className="px-4 py-2.5 text-right text-xs font-medium uppercase tracking-wide text-[#6b7280]">
										Balance
									</th>
								</tr>
							</thead>
							<tbody>
								{invoiceItems.map((inv) => (
									<tr
										key={inv.id}
										className="border-b border-[#f3f4f6] transition-colors duration-150 last:border-0 hover:bg-[#f8f9fb]"
									>
										<td className="px-4 py-3 font-medium text-[#1e1e20]">
											{inv.docNumber ?? `QBO-${inv.id}`}
										</td>
										<td className="px-4 py-3 text-[#1e1e20]">{inv.customerName ?? "Unknown"}</td>
										<td className="px-4 py-3 text-[#6b7280]">{inv.date}</td>
										<td className="px-4 py-3 text-[#6b7280]">{inv.dueDate ?? "--"}</td>
										<td className="px-4 py-3">
											<span
												className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium capitalize ${qboStatusColors[inv.status] ?? "bg-[#f3f4f6] text-[#6b7280]"}`}
											>
												{inv.status}
											</span>
										</td>
										<td className="px-4 py-3 text-right tabular-nums font-medium text-[#1e1e20]">
											{formatDollars(inv.totalAmount)}
										</td>
										<td
											className={`px-4 py-3 text-right tabular-nums ${inv.balance > 0 ? "font-medium text-[#d97706]" : "text-[#6b7280]"}`}
										>
											{formatDollars(inv.balance)}
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				))}
		</div>
	);
}
